import { useMemo } from 'react';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName, SellerRecommendationClickedEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useSeller } from '@reverbdotcom/commons/src/user_hooks';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

export const INSIGHT = {
  RENEW_ELIGIBLE: 'Renew eligible',
  PRICED_ABOVE_AVERAGE: 'Priced above average',
  DIRECT_OFFER: 'Likely to sell with offers',
  TOP_PICK_ELIGIBLE: 'Top pick eligible',
  TOP_PICK_WINNER: 'Top pick winner',
  HIGH_COMPETITION_NEW: 'Low visibility',
} as const;

export type Insight = (typeof INSIGHT)[keyof typeof INSIGHT];

export const RECOMMENDED_ACTION = {
  RENEW: 'Renew',
  PRICE_DROP: 'Price drop',
  ENABLE_DIRECT_OFFERS: 'Enable direct offers',
  INCREASE_BUMP_BID: 'Increase bump bid',
  NONE: 'None',
} as const;

export type RecommendedAction = (typeof RECOMMENDED_ACTION)[keyof typeof RECOMMENDED_ACTION];

export const INSIGHT_TO_RECOMMENDED_ACTION: Record<Insight, RecommendedAction> = {
  [INSIGHT.RENEW_ELIGIBLE]: RECOMMENDED_ACTION.RENEW,
  [INSIGHT.PRICED_ABOVE_AVERAGE]: RECOMMENDED_ACTION.PRICE_DROP,
  [INSIGHT.DIRECT_OFFER]: RECOMMENDED_ACTION.ENABLE_DIRECT_OFFERS,
  [INSIGHT.TOP_PICK_ELIGIBLE]: RECOMMENDED_ACTION.INCREASE_BUMP_BID,
  [INSIGHT.TOP_PICK_WINNER]: RECOMMENDED_ACTION.NONE,
  [INSIGHT.HIGH_COMPETITION_NEW]: RECOMMENDED_ACTION.INCREASE_BUMP_BID,
};

export interface TrackingContext {
  sellerId: string;
  shopId: string;
  secondaryUserModeIsActive: boolean;
  secondaryUserModeIsAvailable: boolean;
}

// Seller context for the tracking events. Callable from any presentment; the
// returned object is passed into the imperative track* helpers below
export function useTrackingContext(): TrackingContext {
  const { sellerId, shopId, secondaryUserModeIsActive, secondaryUserModeIsAvailable } = useSeller();

  return useMemo(
    () => ({
      sellerId: String(sellerId),
      shopId: String(shopId),
      secondaryUserModeIsActive: !!secondaryUserModeIsActive,
      secondaryUserModeIsAvailable: !!secondaryUserModeIsAvailable,
    }),
    [sellerId, shopId, secondaryUserModeIsActive, secondaryUserModeIsAvailable],
  );
}

export interface RecommendationDetails {
  recommendedBumpRate?: number;    // %, for increase_bump_bid insights
  recommendedPriceCents?: number;  // cents, for price_drop insights
}

export interface AppliedDetails {
  appliedBumpRate?: number;      // %, matching recommendedBumpRate's unit -- not the decimal bid
  appliedPriceCents?: number;    // cents, matching recommendedPriceCents' unit
  appliedOfferPercent?: number;  // % off, for direct offers
}

export function useTrackImpression(
  componentName: string,
  insight: Insight,
  listingId: string,
  details?: RecommendationDetails,
) {
  const context = useTrackingContext();

  useViewTracking(
    {
      eventName: MParticleEventName.SellerRecommendationShown,
      componentName,
      insight,
      recommendedAction: INSIGHT_TO_RECOMMENDED_ACTION[insight],
      listingId,
      ...details,
      ...context,
    },
    true,
  );
}

export function buildClickedEvent(
  componentName: string,
  insight: Insight,
  listingId: string,
  context: TrackingContext,
  details?: RecommendationDetails,
): SellerRecommendationClickedEvent {
  return {
    eventName: MParticleEventName.SellerRecommendationClicked,
    componentName,
    insight,
    recommendedAction: INSIGHT_TO_RECOMMENDED_ACTION[insight],
    listingId,
    ...details,
    ...context,
  };
}

export function trackClicked(
  componentName: string,
  insight: Insight,
  listingId: string,
  context: TrackingContext,
  details?: RecommendationDetails,
) {
  trackEvent(buildClickedEvent(componentName, insight, listingId, context, details));
}

export function trackFollowed(
  componentName: string,
  insight: Insight,
  listingId: string,
  context: TrackingContext,
  details?: RecommendationDetails & AppliedDetails,
) {
  trackEvent({
    eventName: MParticleEventName.SellerRecommendationFollowed,
    componentName,
    insight,
    recommendedAction: INSIGHT_TO_RECOMMENDED_ACTION[insight],
    listingId,
    ...details,
    ...context,
  });
}
